import React, { useCallback, useMemo, useState, useEffect } from "react";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import PdfControls from "./PdfControls";
import { Loader2 } from "lucide-react";
import { createPortal } from "react-dom";

interface PdfViewerProps {
  url: string;
  password: string;
}

const PdfViewer: React.FC<PdfViewerProps> = ({ url, password }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setPageWidth] = useState<number>(0);
  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [scale, setScale] = useState(1);

  const file = useMemo(() => {
    return {
      url: url,
    };
  }, [url]);

  const options = useMemo(() => {
    return { password: password };
  }, [password]);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  }, []);

  useEffect(() => {
    const updateWidth = () => {
      if (containerNode) {
        setPageWidth(containerNode.getBoundingClientRect().width);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [containerNode]);

  const containerRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      setContainerNode(node);
    }
  }, []);

  // Handle ESC key press
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsFullScreen(false);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        setPageNumber((prev) => (prev > 1 ? prev - 1 : prev));
      } else if (event.key === "ArrowRight") {
        setPageNumber((prev) => (prev < (numPages ?? 0) ? prev + 1 : prev));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [numPages]);

  // Add zoom handlers
  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.1, 3)); // Max zoom 300%
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.1, 0.5)); // Min zoom 50%
  };

  const handleResetZoom = () => {
    setScale(1);
  };

  // Add a new function to check if we're on mobile
  const isMobile = () => {
    return window.innerWidth <= 1100; // typical mobile breakpoint
  };

  // Update the Page components to include scale
  const renderPage = (pageNum: number) => (
    <Page
      className='max-w-full'
      pageNumber={pageNum}
      width={(pageWidth < 800 ? pageWidth : 400) * scale}
      loading={
        <div className='min-h-[500px] flex items-center justify-center'>
          <div className='flex flex-col gap-2 items-center'>
            <Loader2 className='w-10 h-10 animate-spin' />
            <span>Chargement de la page...</span>
          </div>
        </div>
      }
    />
  );

  // Update the Document components to conditionally render pages
  const renderPages = () => (
    <div className='flex flex-wrap justify-center gap-4'>
      {renderPage(pageNumber)}
      {!isMobile() && pageNumber < (numPages ?? 0) && renderPage(pageNumber + 1)}
    </div>
  );

  const FullScreenModal = () => {
    if (!isFullScreen) return null;

    return createPortal(
      <div className='fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4'>
        <div className='bg-white w-full h-full rounded-lg shadow-xl flex flex-col relative overflow-hidden'>
          <div className='flex-1 overflow-auto p-4'>
            <Document
              className='max-w-full'
              file={file}
              onLoadSuccess={onDocumentLoadSuccess}
              options={options}
              loading={
                <div className='min-h-[500px] flex items-center justify-center'>
                  <div className='flex flex-col gap-2 items-center'>
                    <Loader2 className='w-10 h-10 animate-spin' />
                    <span>Chargement du document...</span>
                  </div>
                </div>
              }
            >
              {renderPages()}
            </Document>

            <PdfControls
              isFullScreen={isFullScreen}
              setIsFullScreen={setIsFullScreen}
              setPage={setPageNumber}
              page={pageNumber}
              maxPage={numPages}
              className='mt-3'
              scale={scale}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onResetZoom={handleResetZoom}
            />
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className='max-w-full relative' ref={containerRef}>
      <PdfControls
        isFullScreen={isFullScreen}
        setIsFullScreen={setIsFullScreen}
        setPage={setPageNumber}
        page={pageNumber}
        maxPage={numPages}
        className='mb-3'
        scale={scale}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onResetZoom={handleResetZoom}
      />

      <Document
        className='max-w-full'
        file={file}
        onLoadSuccess={onDocumentLoadSuccess}
        options={options}
        loading={
          <div className='min-h-[500px] flex items-center justify-center'>
            <div className='flex flex-col gap-2 items-center'>
              <Loader2 className='w-10 h-10 animate-spin' />
              <span>Chargement du document...</span>
            </div>
          </div>
        }
      >
        {renderPages()}
      </Document>

      <PdfControls
        isFullScreen={isFullScreen}
        setIsFullScreen={setIsFullScreen}
        setPage={setPageNumber}
        page={pageNumber}
        maxPage={numPages}
        scale={scale}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onResetZoom={handleResetZoom}
      />

      <FullScreenModal />
    </div>
  );
};

export default PdfViewer;
